/**
 * Zod schemas for product-level JSON fields stored on Prisma models.
 *
 * Per spec §20: every product-level JSON field must include a top-level
 * `schemaVersion` integer, and the application owns lazy read-time adapters
 * keyed by `(fieldName, schemaVersion)` to translate older shapes forward.
 *
 * MVP versions are all `schemaVersion: 1`. Adapters live in api/src/content/adapters/.
 */

import { z } from "zod";

const schemaVersionField = z.object({ schemaVersion: z.number().int().positive() });

/* ---------- blocksOrderJson (PageVersion) ---------- */

export const blocksOrderV1 = schemaVersionField.extend({
  schemaVersion: z.literal(1),
  blocks: z
    .array(
      z.object({
        contentItemId: z.string().min(1),
      }),
    )
    .max(100),
});
export type BlocksOrderV1 = z.infer<typeof blocksOrderV1>;
export const blocksOrderJson = blocksOrderV1;
export type BlocksOrderJson = BlocksOrderV1;

/* ---------- themeJson (Site) ---------- */

/**
 * Shared hex regex for the colour fields. Six lowercase or uppercase hex
 * digits, hash-prefixed. Pulled out so `primary` and the new optional
 * `secondary` / `tertiary` fields share one definition.
 */
const hexString = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/**
 * `themeJsonV1` — operator-editable design tokens persisted on `Site.themeJson`.
 *
 * The shape is intentionally additive: `secondary` and `tertiary` were added
 * AFTER the initial v1 cut (sites #210 + onward expect them) but they are
 * `optional()` so pre-existing rows that only carried `primary` / `background`
 * / `text` keep validating against this same schema — no `schemaVersion`
 * bump, no migration. The renderer's `themeToCssVariables` skips emitting the
 * matching CSS variable when the field is absent, and `BASELINE_CSS` falls
 * back via `color-mix(in srgb, var(--otto-color-primary) ...)` so unchanged
 * pages render identically.
 *
 * Field intent:
 *   - `primary`     — the brand's hero colour: CTAs, buttons, focus rings.
 *   - `secondary`   — supporting accent: link hover, blockquote borders,
 *                     subtle hover states. Often a hue-shifted neighbour of
 *                     `primary` (split-complementary / analogous).
 *   - `tertiary`    — highlight / badge / alert tint: newsletter background,
 *                     FAQ marker, callout strips. A second accent that picks
 *                     up the third leg of a triadic palette.
 *   - `background`  — page background.
 *   - `text`        — primary body text colour. Must clear WCAG AA against
 *                     `background` (the AI generator enforces this; manual
 *                     edits are the operator's responsibility).
 *   - `radius`      — corner radius in pixels (0-64, default 8).
 *   - `fontHeadline` / `fontBody` — pinned to the curated list shipped by the
 *                     renderer (see `web/src/features/sites/ThemeTab.tsx`'s
 *                     `CURATED_FONTS`).
 */
export const themeJsonV1 = schemaVersionField.extend({
  schemaVersion: z.literal(1),
  fontHeadline: z.string().min(1),
  fontBody: z.string().min(1),
  primary: hexString,
  /**
   * Secondary accent for hover states, links, and subtle borders.
   * Optional — sites authored before the field was added validate fine
   * without it; renderer falls back to a `color-mix` derivation of primary.
   */
  secondary: hexString.optional(),
  /**
   * Tertiary highlight for badges, alerts, and accent backgrounds.
   * Optional — same fallback story as `secondary`.
   */
  tertiary: hexString.optional(),
  background: hexString,
  text: hexString,
  radius: z.number().min(0).max(64).default(8),
});
export type ThemeJsonV1 = z.infer<typeof themeJsonV1>;
export const themeJson = themeJsonV1;
export type ThemeJson = ThemeJsonV1;

/**
 * A fresh, neutral `themeJsonV1` stub. Used by seed scripts and SPA forms as a
 * spreadable base so callers don't have to repeat the literal default in
 * three places. Includes `secondary` and `tertiary` so the post-#210 fields
 * are present without forcing optionality at the call site.
 */
export const EMPTY_THEME_JSON_V1: ThemeJsonV1 = {
  schemaVersion: 1,
  fontHeadline: "Fraunces",
  fontBody: "Inter",
  primary: "#2D5BFF",
  secondary: "#7C3AED",
  tertiary: "#F59E0B",
  background: "#FAFAF7",
  text: "#1C1B17",
  radius: 8,
};

/* ---------- brandJson (Site) ---------- */

/**
 * Brand brief envelope (issue #193). Persisted on `Site.brandJson` and
 * inlined into the page-layout (and future theme) AI system prompts so the
 * model's output matches the operator's tone / audience / voice. Nullable
 * at the database level — sites created before #193 keep working and AI
 * prompts simply omit the brand-context prefix when the field is absent.
 *
 * Field caps are intentionally tight so the brief never balloons the
 * prompt's input-token budget (every layout-generate call would pay the
 * cost). The four fields are the smallest set that gives the model the
 * minimum needed steer:
 *   - `tagline` — one-line product positioning statement.
 *   - `tone` — adjective list (e.g. "warm, expert, concise").
 *   - `audience` — who the copy is written for.
 *   - `voiceNotes` — free-text dos/don'ts the editor wants honoured.
 *
 * No PII is intentionally captured here — the field is brand-only.
 * Operators who paste a customer name or email into `voiceNotes` will see
 * it round-tripped, but the SCHEMA does not encourage it; the prompt
 * builder strips no characters and we explicitly DO NOT log the body.
 */
export const brandJsonV1 = schemaVersionField.extend({
  schemaVersion: z.literal(1),
  tagline: z.string().max(140).default(""),
  tone: z.string().max(80).default(""),
  audience: z.string().max(200).default(""),
  voiceNotes: z.string().max(1000).default(""),
  /**
   * Brand intensity dial (issue #232). Strong steering signal for the AI
   * generators — clamps how vibrant the emitted layout / palette / copy
   * gets. `tasteful` favours restraint (fewer columns, neutral accents,
   * line dividers), `playful` adds visual punch (3-4 columns, secondary
   * accent, card dividers), `bold` goes maximalist (3-4 columns, primary
   * accent, spacious density).
   *
   * Optional — pre-#232 rows continue to validate. When absent the prompt
   * builder treats it as a neutral default and the AI Studio post-validator
   * applies no intensity-driven clamps.
   */
  intensity: z.enum(["tasteful", "playful", "bold"]).optional(),
});
export type BrandJsonV1 = z.infer<typeof brandJsonV1>;
export const brandJson = brandJsonV1;
export type BrandJson = BrandJsonV1;

/**
 * A fresh, empty brand brief envelope. Used by the Sites service when a
 * new site is created without an explicit brief — keeps the column
 * populated with a schemaVersioned stub so callers don't have to branch
 * on null. (Sites that already exist pre-#193 stay null until the
 * operator saves a brief from the UI; the prompt builder treats either
 * shape as "no brand context".)
 *
 * `intensity` is intentionally omitted — the empty envelope represents
 * "no operator input yet", and intensity is one of those inputs.
 */
export const EMPTY_BRAND_JSON_V1: BrandJsonV1 = {
  schemaVersion: 1,
  tagline: "",
  tone: "",
  audience: "",
  voiceNotes: "",
};

/* ---------- featureGridLayoutPropsV1 (FEATURE_GRID contentJson.layoutProps) ---------- */

/**
 * Tier-1 dynamic block prototype (issue #232) — FEATURE_GRID layoutProps.
 *
 * Wide structured prop surface that operators (or the AI generator) set on
 * a FEATURE_GRID block's `contentJson.layoutProps`. The renderer emits each
 * field as a `data-*` attribute on the block's `<section>` wrapper; the
 * baseline stylesheet reads them via attribute selectors so the block
 * morphs visually without a code change.
 *
 * Defaults preserve TODAY'S look on legacy rows that don't carry the
 * field at all:
 *   - `columns: 3`           — close to today's `auto-fit` 220px minimum,
 *   - `density: 'comfortable'` — current padding / gap baseline,
 *   - `accent: 'none'`       — no left-rule on each `<li>`,
 *   - `dividerStyle: 'card'`  — current bordered-card look.
 *
 * Additive — no `schemaVersion` bump on the parent `contentJson`. Rows
 * authored before #232 validate identically: their `layoutProps` is
 * absent, the renderer falls back to these defaults, and the visitor
 * sees the same markup the page rendered yesterday.
 */
export const featureGridLayoutPropsV1 = z.object({
  /**
   * Column count for the desktop grid. Below 600px the CSS collapses
   * every value to 1 (see `BASELINE_CSS` mobile media query).
   */
  columns: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).default(3),
  /**
   * Padding + gap density on the grid. `comfortable` matches today's
   * baseline; `compact` tightens for dense feature lists; `spacious` adds
   * breathing room for "premium" / "bold" pages.
   */
  density: z.enum(["compact", "comfortable", "spacious"]).default("comfortable"),
  /**
   * Per-card accent. `primary` / `secondary` / `tertiary` reference the
   * `--otto-color-*` tokens declared on the page's `<html>`; `none`
   * removes the accent rule entirely.
   */
  accent: z.enum(["primary", "secondary", "tertiary", "none"]).default("none"),
  /**
   * Divider style between feature items. `card` (today's look) renders
   * each `<li>` as a bordered card; `line` flattens to a single horizontal
   * rule between items; `none` removes all separation.
   */
  dividerStyle: z.enum(["none", "line", "card"]).default("card"),
});
export type FeatureGridLayoutPropsV1 = z.infer<typeof featureGridLayoutPropsV1>;

/**
 * The shape of a FEATURE_GRID block's `contentJson`. Mirrors the
 * permissive `.passthrough()` baseline used elsewhere (the renderer reads
 * each field defensively) but pins the optional `layoutProps` surface
 * introduced by #232.
 *
 * The parent `schemaVersion` stays at 1 — `layoutProps` is additive and
 * legacy rows without it validate fine (and render with the schema
 * defaults applied).
 *
 * Why this lives next to the other JSON shapes rather than in a new
 * file: the per-block contentJson schemas are intentionally permissive
 * (`.passthrough()`) and this is the first one that pins a structured
 * sub-shape. The renderer + AI-Studio post-validator both `safeParse`
 * `layoutProps` independently, so the per-block "shape" today is really
 * just the `layoutProps` slot — the rest of the FEATURE_GRID payload
 * (heading, features) remains passthrough.
 */
export const featureGridContentJsonV1 = schemaVersionField
  .extend({
    schemaVersion: z.literal(1),
    layoutProps: featureGridLayoutPropsV1.optional(),
  })
  .passthrough();
export type FeatureGridContentJsonV1 = z.infer<typeof featureGridContentJsonV1>;

/* ---------- settingsJson (Site, PageVersion) ---------- */

export const siteSettingsJsonV1 = schemaVersionField.extend({
  schemaVersion: z.literal(1),
  defaultLanguage: z.string().default("en"),
  faviconImageId: z.string().optional(),
  ogImageId: z.string().optional(),
  socialLinks: z.array(z.object({ label: z.string(), url: z.string().url() })).default([]),
  aiCrawlersAllowed: z.boolean().default(false),
});
export type SiteSettingsJsonV1 = z.infer<typeof siteSettingsJsonV1>;

export const pageSettingsJsonV1 = schemaVersionField.extend({
  schemaVersion: z.literal(1),
  noindex: z.boolean().default(false),
  redirectTo: z.string().optional(),
});
export type PageSettingsJsonV1 = z.infer<typeof pageSettingsJsonV1>;

/* ---------- contentJson (ContentVersion) ---------- */
/* Shape varies by BlockType; MVP uses a permissive object validated per-block in slice 2. */

export const contentJsonV1 = schemaVersionField
  .extend({
    schemaVersion: z.literal(1),
  })
  .passthrough();
export type ContentJsonV1 = z.infer<typeof contentJsonV1>;

/* ---------- styleJson (ContentVersion) ---------- */

export const styleJsonV1 = schemaVersionField.extend({
  schemaVersion: z.literal(1),
  align: z.enum(["start", "center", "end"]).optional(),
  background: z.string().optional(),
  paddingY: z.number().min(0).max(256).optional(),
  paddingX: z.number().min(0).max(256).optional(),
});
export type StyleJsonV1 = z.infer<typeof styleJsonV1>;

/* ---------- aiMetaJson (ContentVersion) ---------- */

export const aiMetaJsonV1 = schemaVersionField.extend({
  schemaVersion: z.literal(1),
  generatedBy: z.string().optional(),
  prompt: z.string().optional(),
  model: z.string().optional(),
  inputTokens: z.number().int().nonnegative().optional(),
  outputTokens: z.number().int().nonnegative().optional(),
  costUsd: z.number().nonnegative().optional(),
});
export type AiMetaJsonV1 = z.infer<typeof aiMetaJsonV1>;

/* ---------- contentOverridesJson (ExperimentVariant) ----------
 *
 * Defined in `./experiments.js` so the experiment-related schemas live
 * together. The wrapper carries `schemaVersion: 1` per spec §20 so a
 * future v2 shape has a hinge to swing on; older array-shaped persisted
 * rows are translated forward by adapters at read time.
 *
 * (Historic note: an earlier draft of this file had a bare array shape
 * without `schemaVersion`. Issue #25 promotes the schema to a versioned
 * envelope; the runtime + type now live in `./experiments.ts`.)
 */

/* ---------- metadataJson (AnalyticsEvent / ImageAsset) ---------- */

/**
 * `AnalyticsEvent.metadataJson` schema. Per spec §20 every product-level JSON
 * field carries a `schemaVersion` so future shape migrations have a hinge to
 * swing on. The ingestion endpoint (#22) rejects any payload whose
 * `schemaVersion` is not 1 with a 422 — older / future shapes have no in-place
 * rewrite path; the renderer + client beacon control this top-level field.
 *
 * The body is otherwise free-form (`.passthrough()`) — analytics callers
 * carry arbitrary structured context (e.g. `source: 'server' | 'client'`,
 * experiment-variant ids, scroll depth) and we don't want to gate every new
 * key behind a Zod change.
 */
export const analyticsEventMetadataV1 = schemaVersionField
  .extend({ schemaVersion: z.literal(1) })
  .passthrough();
export type AnalyticsEventMetadataV1 = z.infer<typeof analyticsEventMetadataV1>;
